import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import DataTable from '../components/ResponsiveDataTable';
import { getB2BCoupons, toggleB2BCouponStatus } from '../config/api';

const B2BCoupons = () => {
  const [coupons, setCoupons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    search: '',
    type: 'all',
    status: 'all',
  });
  const [filterText, setFilterText] = useState('');

  useEffect(() => {
    loadCoupons();
  }, [filters]);

  const loadCoupons = async () => {
    setLoading(true);
    try {
      const data = await getB2BCoupons(filters) || {};
      setCoupons(data.data || []);
    } catch (error) {
      console.error('Error loading B2B coupons:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const handleToggleStatus = async (couponId, currentStatus) => {
    try {
      await toggleB2BCouponStatus(couponId, !currentStatus);
      loadCoupons();
      alert(`B2B Coupon ${!currentStatus ? 'activated' : 'deactivated'} successfully!`);
    } catch (error) {
      console.error('Error toggling B2B coupon status:', error);
      alert('Failed to update coupon status. Please try again.');
    }
  };

  const getStatusBadge = (active) => {
    return (
      <span className={active ? 'status-badge status-confirmed' : 'status-badge status-pending'}>
        {active ? 'Active' : 'Inactive'}
      </span>
    );
  };

  const getTypeBadge = (type) => {
    const typeClasses = {
      FIXED: 'status-badge status-shipped',
      PERCENT: 'status-badge status-delivered',
    };
    return (
      <span className={typeClasses[type] || 'status-badge'}>
        {type === 'FIXED' ? 'Fixed Amount' : 'Percentage'}
      </span>
    );
  };

  const formatValue = (type, value) => {
    return type === 'FIXED' ? `₹${parseFloat(value).toFixed(2)}` : `${value}%`;
  };

  const isExpired = (validTill) => {
    return new Date(validTill) < new Date();
  };

  const columns = [
    {
      name: 'Coupon Code',
      selector: row => row.code,
      sortable: true,
      width: '150px',
      cell: row => (
        <Link
          to={`/b2b-coupons/${row.id}`}
          style={{ textDecoration: "none", color: "inherit" }}
        >
          <div style={{
            fontWeight: '600',
            color: '#0d6efd',
            fontFamily: 'monospace',
            fontSize: '14px'
          }}>
            {row.code}
          </div>
        </Link>
      ),
    },
    {
      name: 'Type',
      selector: row => row.type,
      sortable: true,
      cell: row => getTypeBadge(row.type),
      width: '150px',
    },
    {
      name: 'Value',
      selector: row => row.value,
      sortable: true,
      cell: row => (
        <div style={{ fontWeight: '600', color: '#212529' }}>
          {formatValue(row.type, row.value)}
        </div>
      ),
      width: '140px',
    },
    {
      name: 'Min Cart Value',
      selector: row => row.min_cart_value,
      sortable: true,
      cell: row => (
        <div style={{ color: '#6c757d' }}>
          {row.min_cart_value ? `₹${parseFloat(row.min_cart_value).toFixed(2)}` : 'No minimum'}
        </div>
      ),
      width: '160px',
    },
    {
      name: 'Max Discount',
      selector: row => row.max_discount,
      sortable: true,
      cell: row => (
        <div style={{ color: '#6c757d' }}>
          {row.max_discount ? `₹${parseFloat(row.max_discount).toFixed(2)}` : 'No limit'}
        </div>
      ),
      width: '160px',
    },
    {
      name: 'Usage',
      selector: row => row.used_count,
      sortable: true,
      cell: row => (
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontWeight: '600', color: '#212529', alignItems: 'baseline', gap: '4px', display: 'flex', justifyContent: 'center' }}>
            <span>{row.used_count || 0}</span>
            <span style={{ fontSize: '12px', color: '#6c757d' }}>
              / {row.usage_limit ?? '∞'}
            </span>
          </div>
          {row.valid_for_initial && (
            <div style={{ fontSize: '11px', color: '#0d6efd' }}>
              Initial: {row.valid_for_initial}
            </div>
          )}
        </div>
      ),
      width: '120px',
    },
    {
      name: 'Valid Till',
      selector: row => row.valid_till,
      sortable: true,
      cell: row => (
        <div style={{
          color: isExpired(row.valid_till) ? '#dc3545' : '#212529',
          fontWeight: isExpired(row.valid_till) ? '600' : 'normal'
        }}>
          {new Date(row.valid_till).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
          })}
          {isExpired(row.valid_till) && (
            <div style={{ fontSize: '12px', color: '#dc3545' }}>Expired</div>
          )}
        </div>
      ),
      width: '130px',
    },
    {
      name: 'Status',
      selector: row => row.active,
      sortable: true,
      cell: row => getStatusBadge(row.active),
      width: '120px',
    },
    {
      name: 'Actions',
      cell: row => (
        <div style={{ display: 'flex', gap: '8px' }}>
          <Link
            to={`/b2b-coupons/${row.id}`}
            className="btn btn-primary"
            style={{
              fontSize: '12px',
              padding: '6px 12px',
              textDecoration: 'none'
            }}
          >
            View
          </Link>
          <button
            onClick={() => handleToggleStatus(row.id, row.active)}
            className={`btn ${row.active ? 'btn-warning' : 'btn-success'}`}
            style={{
              fontSize: '12px',
              padding: '6px 12px'
            }}
          >
            {row.active ? 'Deactivate' : 'Activate'}
          </button>
        </div>
      ),
      ignoreRowClick: true,
      allowOverflow: true,
      button: true,
      width: '180px',
    },
  ];

  const filteredItems = coupons.filter(item => {
    const searchText = filterText.toLowerCase();
    return (
      item.code.toLowerCase().includes(searchText) ||
      item.type.toLowerCase().includes(searchText)
    );
  });

  const subHeaderComponent = useMemo(() => {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        width: '100%',
        padding: '8px 0'
      }}>
        <div style={{ position: 'relative', width: '100%', maxWidth: '300px' }}>
          <input
            type="text"
            placeholder="Search B2B coupons..."
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            style={{
              padding: '10px 14px',
              border: '2px solid #e2e8f0',
              borderRadius: '6px',
              fontSize: '14px',
              width: '100%',
              transition: 'all 0.3s ease',
            }}
            onFocus={(e) => {
              e.target.style.borderColor = '#0d6efd';
              e.target.style.boxShadow = '0 0 0 2px rgba(13, 110, 253, 0.25)';
            }}
            onBlur={(e) => {
              e.target.style.borderColor = '#ced4da';
              e.target.style.boxShadow = 'none';
            }}
          />
        </div>
      </div>
    );
  }, [filterText]);

  const customStyles = {
    header: {
      style: { minHeight: '56px', paddingLeft: '0px', paddingRight: '0px' },
    },
    headRow: {
      style: {
        borderTopStyle: 'solid', borderTopWidth: '1px', borderTopColor: '#dee2e6',
        backgroundColor: '#f8f9fa',
      },
    },
    headCells: {
      style: {
        '&:not(:last-of-type)': { borderRightStyle: 'solid', borderRightWidth: '1px', borderRightColor: '#dee2e6' },
        backgroundColor: '#f8f9fa', fontWeight: '600', fontSize: '14px',
        color: '#495057', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '16px',
      },
    },
    cells: {
      style: {
        '&:not(:last-of-type)': { borderRightStyle: 'solid', borderRightWidth: '1px', borderRightColor: '#dee2e6' },
        padding: '16px', fontSize: '14px',
      },
    },
    rows: {
      style: { '&:hover': { backgroundColor: '#f8f9fa', cursor: 'pointer' }, borderBottomColor: '#dee2e6' },
    },
    pagination: {
      style: { borderTop: '1px solid #dee2e6', padding: '16px' },
    },
  };

  return (
    <div className="container">
      <div className="page-header">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <h1 className="page-title">B2B Coupons Management</h1>
            <p className="page-subtitle">Create and manage discount coupons for B2B customers</p>
          </div>
          <div style={{ display: 'flex', gap: '12px' }}>
            <Link
              to="/b2b-coupons/create"
              className="btn btn-success"
              style={{
                fontSize: '14px',
                padding: '12px 20px',
                textDecoration: 'none'
              }}
            >
              + Create B2B Coupon
            </Link>
          </div>
        </div>
      </div>

      <div className="filters-card">
        <h3 className="filters-title">Filter B2B Coupons</h3>
        <div className="filters">
          <div className="filter-group">
            <label className="form-label">Type</label>
            <select
              name="type"
              className="form-control"
              value={filters.type}
              onChange={handleFilterChange}
            >
              <option value="all">All Types</option>
              <option value="FIXED">Fixed Amount</option>
              <option value="PERCENT">Percentage</option>
            </select>
          </div>

          <div className="filter-group">
            <label className="form-label">Status</label>
            <select
              name="status"
              className="form-control"
              value={filters.status}
              onChange={handleFilterChange}
            >
              <option value="all">All Status</option>
              <option value="true">Active</option>
              <option value="false">Inactive</option>
            </select>
          </div>
        </div>
      </div>

      <div className="card">
        <DataTable
          columns={columns}
          data={filteredItems}
          progressPending={loading}
          pagination
          paginationResetDefaultPage={false}
          subHeader
          subHeaderComponent={subHeaderComponent}
          selectableRows={false}
          customStyles={customStyles}
          highlightOnHover
          pointerOnHover
          responsive
          striped
          paginationPerPage={10}
          paginationRowsPerPageOptions={[5, 10, 15, 20, 25, 50]}
          noDataComponent={
            <div style={{
              padding: '60px 40px',
              textAlign: 'center',
              color: '#6c757d',
              fontSize: '16px'
            }}>
              <div style={{ fontWeight: '600', marginBottom: '8px' }}>No B2B coupons found</div>
              <div>Create your first B2B coupon to get started</div>
            </div>
          }
          defaultSortFieldId="valid_till"
          defaultSortAsc={false}
        />
      </div>
    </div>
  );
};

export default B2BCoupons;
